import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, debounceTime, defer, map, Observable, of, switchMap, tap } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { MovieListHandlerComponent } from '@usheru-hff/community-elements/feature-movie-list-handler';
import { MovieRatingHandlerComponent } from '@usheru-hff/community-elements/feature-movie-rating-handler';
import { UsheruApi } from '@usheru-hff/shared/data-access-backend';
import { List, Movie, StreamingPlatform, Tag, User, WatchedMovie } from '@usheru-hff/shared/domain';
import { ShareComponent } from '@usheru-hff/shared/feature-share';
import { ModalService } from '@usheru-hff/shared/utils-modal';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

const enum ENDPOINTS {
  wishlist = '/movie-actions/wishlist/',
  watched = '/movie-actions/watched/',
  list = '/movie-actions/list/',
  searchList = '/user-actions/search/list/',
  searchUser = '/movie-actions/search/user/',
  moviesByList = '/movie-actions/list/movies/',
  rating = '/movie-actions/rating/',
  follow = '/movie-actions/follow/',
  userStatus = '/movie-actions/user-status/',
  followStreamingPlatform = '/user-actions/follow/streamings/',
  followTag = '/user-actions/follow/tags/',
  followList = '/user-actions/follow/users/lists/',
  followUser = '/user-actions/follow/users/',
  followMe = '/movie-actions/follow-me/users/'
}

@Injectable({
  providedIn: 'root'
})
export class UserInteractionService {
  private readonly PAGE_OFFSET = 24;
  constructor(
    private usheruApi: UsheruApi,
    private authService: AuthService,
    private http: HttpClient,
    @Inject(ENVIRONMENT) private env: Environment,
    private modalService: ModalService,
    private translationsService: TranslationsService
  ) {}

  getWishList(): Observable<Movie[]> {
    const body = { trLanguage: this.language };
    return this.http
      .get<Movie[]>(`${this.env.api?.url}${ENDPOINTS.wishlist}`, {
        headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken()),
        params: this.usheruApi.filterToParams(body)
      })
      .pipe(map(movies => movies.map(movie => ({ ...movie, inMyWhislist: true, inMyWishlist: true }))));
  }

  /**
   * @description Adds/Saves movie to watchlist/wishlist. It also uses a messageService to provide feedback to the user.
   * @param movieId
   * @returns Observable<Movie[]> an array of movies
   */
  addMovieToWishList(idMovie: string): Observable<Movie[]> {
    // return of({ success: true });
    const body = { idMovie, trLanguage: this.language };
    return this.http.put<Movie[]>(`${this.env.api?.url}${ENDPOINTS.wishlist}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  /**
   * @description Removes a movie from watchlist/wishlist. It also uses a messageService to provide feedback to the user.
   * @param movieId
   * @returns Observable<Movie[]> an array of movies
   */
  removeMovieFromWishList(idMovie: string): Observable<Movie[]> {
    // return of({ success: true });
    const body = { idMovie, trLanguage: this.language };
    return this.http.post<Movie[]>(`${this.env.api?.url}${ENDPOINTS.wishlist}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  getWatchedMovies(): Observable<WatchedMovie[]> {
    const body = { trLanguage: this.language };
    return this.http
      .get<WatchedMovie[]>(`${this.env.api?.url}${ENDPOINTS.watched}`, {
        headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken()),
        params: this.usheruApi.filterToParams(body)
      })
      .pipe(
        map(watchedMovies =>
          watchedMovies.map(watchedMovie => ({ ...watchedMovie, movie: { ...watchedMovie.movie, inMyWatched: true } }))
        )
      );
  }

  /**
   * @description Adds/Saves movie to watched list, and adds a rating 0 - 1. It also uses a messageService to provide feedback to the user.
   * @param movieId string
   * @param rating number 0.0 - 1.0
   * @returns Observable<Movie[]>
   */
  addMovieToWatched(params: { idMovie: string | number; rank?: number; comment?: string }): Observable<WatchedMovie[]> {
    // return of({ success: true });
    const { idMovie, rank } = params;
    const body = { idMovie, rank, trLanguage: this.language };
    return this.http.put<WatchedMovie[]>(`${this.env.api?.url}${ENDPOINTS.watched}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  /**
   * @description Removes movie from watched list. It also uses a messageService to provide feedback to the user.
   * @param movieId string
   * @returns Observable<Movie[]>
   */
  removeMovieFromWatched(idMovie: string): Observable<WatchedMovie[]> {
    const body = { idMovie, trLanguage: this.language };
    return this.http.post<WatchedMovie[]>(`${this.env.api?.url}${ENDPOINTS.watched}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  getLists(params: { username?: string | undefined; slug?: string | undefined }): Observable<List[]> {
    // return of([]);
    let isOwnLists = false;
    const { username, slug } = params;
    const body = { trLanguage: this.language };
    if (username) {
      isOwnLists = this.authService.isUserLoggedIn(username);
      body['username'] = username;
    } else {
      body['slug'] = slug;
    }

    return this.http.get<List[]>(`${this.env.api?.url}${ENDPOINTS.list}`, {
      headers: isOwnLists
        ? this.usheruApi.generateBearerHeader(this.authService?.accessToken())
        : this.usheruApi.getStandardHeader(),
      params: body ? this.usheruApi.filterToParams(body) : undefined
    });
  }

  /**
   * @description Adds a list to user lists. It also uses a messageService to provide feedback to the user.
   * @param params {listSlug: string, visible: boolean}
   * @returns Observable<List[]> an array of lists
   */
  addList(params: { name: string; visible: boolean; description?: string }): Observable<List[]> {
    // return of([]);
    const { name, visible } = params;
    const description = params.description || '';

    const body = { name, visible, description, trLanguage: this.language };
    return this.http.put<List[]>(`${this.env.api?.url}${ENDPOINTS.list}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  /**
   * @description Updates a list
   * @param params {newName: string, slug: string, description: string}
   * @returns Observable<List[]> an array of lists
   */
  updateList(params: {
    newName: string | undefined;
    slug: string | undefined;
    description?: string | undefined;
  }): Observable<List[]> {
    // return of([]);
    const { newName, slug } = params;
    const description = params.description || '';

    const body = { newName, slug, description, trLanguage: this.language };
    return this.http.put<List[]>(`${this.env.api?.url}${ENDPOINTS.list}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  /**
   * @description Removes a list to user lists. It also uses a messageService to provide feedback to the user.
   * @param listName: string
   * @returns Observable<List[]> an array of lists
   */
  removeList(slug: string): Observable<List[]> {
    // return of({ success: true });
    const body = { slug, trLanguage: this.language };
    return this.http.post<List[]>(`${this.env.api?.url}${ENDPOINTS.list}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  /**
   * @description Adds a movie to an specific user list. It also uses a messageService to provide feedback to the user.
   *  @param idMovie: string
   * @param listSlug: string
   *  @returns Observable<Movie[]> an array of lists
   */
  addMovieToList(idMovie: string, slug: string): Observable<Movie[]> {
    // return of({ success: true });
    const body = { idMovie, slug, trLanguage: this.language };
    return this.http.put<Movie[]>(`${this.env.api?.url}${ENDPOINTS.moviesByList}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  /**
   * @description Removes a movie to an specific user list. It also uses a messageService to provide feedback to the user.
   *  @param idMovie: string
   * @param uuid: string
   *  @returns Observable<Movie[]> an array of lists
   */
  removeMovieFromList(idMovie: string, slug: string): Observable<Movie[]> {
    // return of({ success: true });
    const body = { idMovie, slug, trLanguage: this.language };
    return this.http.post<Movie[]>(`${this.env.api?.url}${ENDPOINTS.moviesByList}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  searchLists(name: string): Observable<List[]> {
    const body = { name, trLanguage: this.language };
    const headers = this.authService.isLoggedIn()
      ? this.usheruApi.generateBearerHeader(this.authService?.accessToken())
      : this.usheruApi.getStandardHeader();
    return this.http.get<List[]>(`${this.env.api?.url}${ENDPOINTS.searchList}`, {
      headers,
      params: this.usheruApi.filterToParams(body)
    });
  }

  /**
   * @description Searches for users by username.
   * @param username: string
   * @param extraParams: { page?: number; pageOffset?: number }
   * @returns Observable<User[]> an array of users
   */
  searchUser(username: string, extraParams?: { page?: number; pageOffset?: number }): Observable<User[]> {
    const params = { pageOffset: this.PAGE_OFFSET, ...extraParams, ...(username ? { username } : {}) };
    const headers = this.authService.isLoggedIn()
      ? this.usheruApi.generateBearerHeader(this.authService?.accessToken())
      : this.usheruApi.getStandardHeader();
    return this.http.get<User[]>(`${this.env.api?.url}${ENDPOINTS.searchUser}`, {
      headers,
      params
    });
  }

  /**
   * @description Opens up a modal to search for lists to add a movie to.
   * @param movie: Movie
   * @param user: User
   */
  searchMyLists(movie: Movie, user: User): void {
    this.modalService.openModal(MovieListHandlerComponent, { params: { movie, user } });
  }

  /**
   * @description Opens up a modal to share.
   * @param movie: Movie
   */
  shareMovieDetail(movie: Movie): void {
    this.modalService.openModal(ShareComponent, { params: { movie } });
  }

  /**
   * @description Opens up a modal to rate a movie.
   * @param movie: Movie
   */
  getMovieRating(movie: Movie): void {
    this.modalService.openModal(MovieRatingHandlerComponent, { params: { movie } });
  }

  getFollowedStreamingPlatforms(): Observable<StreamingPlatform[]> {
    return this.http
      .get<StreamingPlatform[]>(`${this.env.api?.url}${ENDPOINTS.followStreamingPlatform}`, {
        headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
      })
      .pipe(
        map(streamingPlatforms =>
          streamingPlatforms.map(streamingPlatform => ({ ...streamingPlatform, inMyStreamingPlatforms: true }))
        )
      );
  }
  followStreamingPlatform(platformId: string | number): Observable<StreamingPlatform[]> {
    const body = { id: platformId };
    return this.http.put<StreamingPlatform[]>(`${this.env.api?.url}${ENDPOINTS.followStreamingPlatform}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }
  unfollowStreamingPlatform(platformId: string | number): Observable<StreamingPlatform[]> {
    const body = { id: platformId };
    return this.http.post<StreamingPlatform[]>(`${this.env.api?.url}${ENDPOINTS.followStreamingPlatform}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  getTagsFollowed(tagType: string): Observable<Tag[]> {
    const params = { type: tagType };
    return this.http
      .get<Tag[]>(`${this.env.api?.url}${ENDPOINTS.followTag}`, {
        headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken()),
        params
      })
      .pipe(map(tags => tags.map(tag => ({ ...tag, inMyTags: true }))));
  }

  followTag(tagId: string | number): Observable<Tag[]> {
    const body = { id: tagId };
    return this.http.put<Tag[]>(`${this.env.api?.url}${ENDPOINTS.followTag}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }
  unfollowTag(tagId: string | number): Observable<Tag[]> {
    const body = { id: tagId };
    return this.http.post<Tag[]>(`${this.env.api?.url}${ENDPOINTS.followTag}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  getUsersFollowed(): Observable<User[]> {
    return this.http.get<User[]>(`${this.env.api?.url}${ENDPOINTS.followUser}`, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }
  getUserFollowingMe(): Observable<User[]> {
    const params = {};
    return this.http.get<User[]>(`${this.env.api?.url}${ENDPOINTS.followMe}`, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken()),
      params: this.usheruApi.filterToParams(params)
    });
  }

  followUser(username: string | number): Observable<User[]> {
    const body = { username };
    return this.http.put<User[]>(`${this.env.api?.url}${ENDPOINTS.followUser}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  unfollowUser(username: string | number): Observable<User[]> {
    const body = { username };
    return this.http.post<User[]>(`${this.env.api?.url}${ENDPOINTS.followUser}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  getFollowedLists(): Observable<List[]> {
    const body = { trLanguage: this.language };
    return this.http
      .get<List[]>(`${this.env.api?.url}${ENDPOINTS.followList}`, {
        headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken()),
        params: this.usheruApi.filterToParams(body)
      })
      .pipe(map(lists => lists.map(list => ({ ...list, inMyLists: true }))));
  }

  followList(slug: string): Observable<List[]> {
    const body = { slug, trLanguage: this.language };
    return this.http.put<List[]>(`${this.env.api?.url}${ENDPOINTS.followList}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }
  unfollowList(slug: string): Observable<List[]> {
    const body = { slug, trLanguage: this.language };
    return this.http.post<List[]>(`${this.env.api?.url}${ENDPOINTS.followList}`, body, {
      headers: this.usheruApi.generateBearerHeader(this.authService?.accessToken())
    });
  }

  rateMovie(idMovie: string | number, rating: number): Observable<WatchedMovie[]> {
    return this.addMovieToWatched({ idMovie, rank: rating });
  }

  get language(): string {
    return this.translationsService.language?.code;
  }
}
